/**
 * subjects.js — single source of truth for the canonical 11-subject
 * list (Phase 116, Feb 2026).
 *
 * The Atomic Edu UI used to bundle "The Arts" (one entry) and
 * "Health and Physical Education" (one entry) into single picks.
 * Ontario elementary teachers are assigned subjects in fragments —
 * rotary, prep coverage, split assignments — so bundling forces a
 * teacher who teaches only Visual Arts (and no other arts strand) to
 * either claim all four arts strands or none. The split list lets
 * each teacher select precisely their real teaching load.
 *
 * Where this is used:
 *   - TeacherProfileModal classroom-profile checklist
 *   - LongRangePlan canvas (rows / suggestions / persistence keys)
 *   - CoverageTracker subject sections (Phase 116.B filter)
 *   - Gradebook subject sidebar (Phase 116.B filter)
 *
 * Where this is NOT used:
 *   - Report Card Builder (explicitly exempt per spec — teachers may
 *     legitimately draft comments in subjects they don't personally
 *     teach, e.g. handing a Music comment to a colleague)
 *   - Generator subject pickers (a teacher might generate a one-off
 *     lesson outside their normal load)
 *
 * Each entry carries:
 *   - `key`        — stable id used for LRP `subjects.{key}` storage
 *                    and Coverage / Gradebook visibility filtering.
 *   - `label`      — display name on every surface.
 *   - `group`      — visual grouping hint (only used by the LRP
 *                    arts sub-strand rows today).
 *   - `legacy_keys`— LRP-internal keys whose stored data should still
 *                    surface under this canonical subject. Lets us
 *                    rename the displayed label without migrating any
 *                    of the user's existing units.
 *   - `legacy_profile_labels` — names that may already exist in a
 *                    teacher's saved `subjects_taught` and should
 *                    expand into this canonical subject for filter
 *                    purposes (e.g. legacy "The Arts" expands into
 *                    Dance + Drama + Music + Visual Arts).
 */

export const CANONICAL_SUBJECTS = [
  { key: "language", label: "Language", legacy_keys: [], legacy_profile_labels: ["Language"] },
  { key: "math", label: "Mathematics", legacy_keys: [], legacy_profile_labels: ["Mathematics"] },
  { key: "science", label: "Science and Technology", legacy_keys: [], legacy_profile_labels: ["Science and Technology"] },
  {
    key: "social_studies",
    label: "Social Studies / History and Geography",
    legacy_keys: [],
    legacy_profile_labels: ["Social Studies", "History", "Geography", "Social Studies / History and Geography"],
  },
  { key: "dance", label: "Dance", group: "Arts", legacy_keys: [], legacy_profile_labels: ["Dance"] },
  { key: "drama", label: "Drama", group: "Arts", legacy_keys: [], legacy_profile_labels: ["Drama"] },
  { key: "music", label: "Music", group: "Arts", legacy_keys: [], legacy_profile_labels: ["Music"] },
  { key: "visual_art", label: "Visual Arts", group: "Arts", legacy_keys: ["visual_art"], legacy_profile_labels: ["Visual Arts", "Visual Art"] },
  {
    key: "health",
    label: "Health",
    legacy_keys: [],
    // Legacy combined entry — a teacher who pre-Phase-116 checked
    // "Health and Physical Education" should see both Health and PE
    // expand into the visible set so nothing disappears overnight.
    legacy_profile_labels: ["Health", "Health and Physical Education"],
  },
  {
    key: "phys_ed",
    label: "Physical Education",
    legacy_keys: [],
    legacy_profile_labels: ["Physical Education", "Health and Physical Education"],
  },
  { key: "french", label: "French as a Second Language", legacy_keys: [], legacy_profile_labels: ["French as a Second Language"] },
];

// Quick-access map for label lookup by key.
export const SUBJECT_LABEL_BY_KEY = CANONICAL_SUBJECTS.reduce((acc, s) => {
  acc[s.key] = s.label;
  return acc;
}, {});

// Quick-access map for key lookup by label (canonical + any legacy
// label alias) — used to normalise stored profile selections.
const _LABEL_TO_KEY = (() => {
  const m = {};
  for (const s of CANONICAL_SUBJECTS) {
    m[s.label.toLowerCase()] = s.key;
    for (const alias of s.legacy_profile_labels || []) {
      const k = alias.toLowerCase();
      // Bundled legacy labels (e.g. "Health and Physical Education")
      // map to the FIRST subject in iteration order — the expand step
      // below is what splits them into the full set.
      if (!(k in m)) m[k] = s.key;
    }
  }
  return m;
})();

/**
 * Normalise an arbitrary `subjects_taught` array (mixed legacy /
 * canonical labels) into a Set of canonical keys.
 *
 * Examples:
 *   ["The Arts", "Mathematics"]
 *     → {"dance", "drama", "music", "visual_art", "math"}
 *   ["Health and Physical Education"]
 *     → {"health", "phys_ed"}
 *   ["Visual Arts"]
 *     → {"visual_art"}
 *   ["Social Studies", "History", "Geography"]
 *     → {"social_studies"}      // all collapse into one bucket
 *
 * Returns an empty set when the input is empty — callers treat that
 * as "show everything" (the default for a brand-new teacher who
 * hasn't filled out the Class Profile yet).
 */
export const profileSubjectsAsKeys = (subjectsTaught) => {
  const out = new Set();
  if (!Array.isArray(subjectsTaught) || subjectsTaught.length === 0) {
    return out;
  }
  for (const raw of subjectsTaught) {
    if (!raw || typeof raw !== "string") continue;
    const lower = raw.trim().toLowerCase();
    // Bundled legacy labels expand into every canonical key whose
    // legacy_profile_labels include this alias.
    let matched = false;
    for (const s of CANONICAL_SUBJECTS) {
      const aliases = (s.legacy_profile_labels || []).map((a) => a.toLowerCase());
      if (aliases.includes(lower) || s.label.toLowerCase() === lower) {
        out.add(s.key);
        matched = true;
      }
    }
    if (!matched) {
      // Unknown subject string — could be a freshly-renamed canonical
      // entry that simply isn't in the alias table yet. Fall back to
      // the simple key lookup if anything matches; otherwise drop.
      const k = _LABEL_TO_KEY[lower];
      if (k) out.add(k);
    }
  }
  return out;
};

/**
 * Decide whether a given canonical subject key should be visible for
 * a teacher with the given `subjects_taught` profile.
 *
 * Rule: empty profile → everything visible (default); otherwise only
 * the canonical keys in the normalised set are visible.
 */
export const isSubjectVisible = (key, subjectsTaught) => {
  if (!key) return true;
  const set = profileSubjectsAsKeys(subjectsTaught);
  if (set.size === 0) return true; // default-show-all
  return set.has(key);
};

/**
 * Convert a teacher's current canonical-key Set back to the array
 * shape persisted to `users.subjects_taught` — i.e. the canonical
 * label strings ("Dance", "Mathematics", "Health", …). Drops keys
 * the canonical list no longer knows about.
 */
export const keysToProfileLabels = (keys) => {
  const out = [];
  for (const s of CANONICAL_SUBJECTS) {
    if (keys && keys.has && keys.has(s.key)) out.push(s.label);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Phase 116.B — Class Profile as master filter.
//
// The various surfaces (Coverage Tracker, Gradebook, LRP) key their
// data three different ways:
//   - LRP/Gradebook `subject_key`  : lowercase keys like "language",
//                                    "math", "arts" (legacy), "health"
//                                    (legacy bundled), "dance",
//                                    "phys_ed", "fsl".
//   - Coverage Tracker             : curriculum-side display strings
//                                    ("The Arts", "Health and Physical
//                                    Education", "Social Studies", …).
//   - Class Profile (canonical)    : the 11 canonical keys above.
//
// These tables bridge between them so the visibility filter can be
// expressed once and applied everywhere.
// ---------------------------------------------------------------------------

// canonical key → curriculum-side subject strings (a canonical pick
// "Visual Arts" surfaces under "The Arts" because the curriculum data
// is still bundled at that level; canonical "Social Studies / History
// and Geography" surfaces under three curriculum subjects depending
// on grade band).
export const CANONICAL_TO_CURRICULUM = {
  language: ["Language"],
  math: ["Mathematics"],
  science: ["Science and Technology"],
  social_studies: ["Social Studies", "History", "Geography"],
  dance: ["The Arts"],
  drama: ["The Arts"],
  music: ["The Arts"],
  visual_art: ["The Arts"],
  health: ["Health and Physical Education"],
  phys_ed: ["Health and Physical Education"],
  french: ["French as a Second Language"],
};

// LRP / Gradebook subject_key → list of canonical keys it represents.
// A legacy bundled key (`arts`, `health`) represents multiple canonical
// keys; either checked → visible. New split keys map 1:1.
export const LRP_KEY_TO_CANONICAL = {
  language: ["language"],
  math: ["math"],
  science: ["science"],
  social_studies: ["social_studies"],
  arts: ["dance", "drama", "music", "visual_art"],        // legacy bundled
  health: ["health", "phys_ed"],                          // legacy bundled
  dance: ["dance"],
  drama: ["drama"],
  music: ["music"],
  visual_art: ["visual_art"],
  phys_ed: ["phys_ed"],
  french: ["french"],
  fsl: ["french"],
};

/**
 * Visibility check for an LRP/Gradebook `subject_key`. The filter
 * passes if ANY canonical key that this LRP key represents is in the
 * teacher's `subjects_taught`. Empty profile → default-show-all.
 */
export const isLrpSubjectKeyVisible = (lrpKey, subjectsTaught) => {
  if (!lrpKey) return false;
  const set = profileSubjectsAsKeys(subjectsTaught);
  if (set.size === 0) return true;
  const canonicals = LRP_KEY_TO_CANONICAL[lrpKey] || [];
  return canonicals.some((c) => set.has(c));
};

/**
 * Visibility check for a curriculum-side subject display string
 * ("The Arts", "Health and Physical Education", …). The filter
 * passes if ANY canonical key whose CANONICAL_TO_CURRICULUM mapping
 * contains the given curriculum subject is in the teacher's
 * `subjects_taught`. Empty profile → default-show-all.
 */
export const isCurriculumSubjectVisible = (currSubject, subjectsTaught) => {
  if (!currSubject) return false;
  const set = profileSubjectsAsKeys(subjectsTaught);
  if (set.size === 0) return true;
  for (const [canonKey, currList] of Object.entries(CANONICAL_TO_CURRICULUM)) {
    if (currList.includes(currSubject) && set.has(canonKey)) return true;
  }
  return false;
};
